import { Step, StepIconProps, StepLabel, Stepper, SxProps, useMediaQuery, useTheme } from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import EuroIcon from '@mui/icons-material/Euro';
import { useAtomValue } from 'jotai';
import { Order_Status_Enum } from '@kheops/graphql';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { contextToActionNeededStatusMap, orderAtom } from '../../state/state';
import { currentContextAtom } from '../../../state';
import { getOrderStatusIntl } from '../../../common/utils/order.utils';
import CustomStepIcon from './CustomStepIcon';

enum OrderMainStepsValues {
  VALIDATION = 'VALIDATION',
  DELIVERY = 'DELIVERY',
  PAYMENT = 'PAYMENT',
  FINISHED = 'FINISHED',
}

type OrderStatusHandledByStepper = Exclude<Order_Status_Enum, Order_Status_Enum.OrderArchived | Order_Status_Enum.OrderCancelled | Order_Status_Enum.ClosedButContestedPayment>;

type OrderStatusToStepMap = {
  [status in OrderStatusHandledByStepper]: OrderMainStepsValues
};

interface OrderStatusStepperProps {
  sx?: SxProps;
}

const orderStatusToStepMap : OrderStatusToStepMap = {
  order_to_be_validated_by_buyer: OrderMainStepsValues.VALIDATION,
  order_to_be_validated_by_supplier: OrderMainStepsValues.VALIDATION,
  order_to_be_prepared_by_supplier: OrderMainStepsValues.DELIVERY,
  order_to_be_delivered_by_supplier: OrderMainStepsValues.DELIVERY,
  order_to_be_received_by_buyer: OrderMainStepsValues.DELIVERY,
  order_to_be_billed_by_supplier: OrderMainStepsValues.PAYMENT,
  order_to_be_paid_by_buyer: OrderMainStepsValues.PAYMENT,
  order_being_paid_by_buyer: OrderMainStepsValues.PAYMENT,
  order_closed: OrderMainStepsValues.FINISHED,
};

const stepsInfoMap: {
  [step in OrderMainStepsValues]: {
    icon: React.ReactElement,
    label: string;
  }
} = {
  VALIDATION: {
    icon: <AccessTimeIcon />,
    label: 'order:validation',
  },
  DELIVERY: {
    icon: <LocalShippingOutlinedIcon />,
    label: 'common:delivery',
  },
  PAYMENT: {
    icon: <EuroIcon />,
    label: 'order:payment',
  },
  FINISHED: {
    icon: <ThumbUpAltOutlinedIcon />,
    label: 'order:completed_one',
  },
};

const orderedSteps = [
  OrderMainStepsValues.VALIDATION,
  OrderMainStepsValues.DELIVERY,
  OrderMainStepsValues.PAYMENT,
  OrderMainStepsValues.FINISHED,
];

function OrderStepIcon(props: StepIconProps): React.JSX.Element {
  const { realm } = useAtomValue(currentContextAtom);
  const order = useAtomValue(orderAtom);

  return (
    <CustomStepIcon
      canEditStep={contextToActionNeededStatusMap.get(realm)!.includes(order.status)}
      {...props}
    />
  );
}

export default function OrderStatusStepper({ sx }: OrderStatusStepperProps): React.JSX.Element {
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['common', 'order', 'order-status']);
  const order = useAtomValue(orderAtom);
  const { realm } = useAtomValue(currentContextAtom);
  const activeStep = orderStatusToStepMap[order.status as OrderStatusHandledByStepper];

  const getStepLabel = (step: OrderMainStepsValues): string | undefined => {
    if (isDownLg) {
      return;
    }

    return step === activeStep
      ? t(`order-status:${getOrderStatusIntl(order.status, realm)}`)
      : t(stepsInfoMap[step].label);
  };

  const steps = orderedSteps.map((step) => {
    return (
      <Step
        key={step}
      >
        <StepLabel
          slots={{
            stepIcon: OrderStepIcon,
          }}
          slotProps={{
            stepIcon: {
              icon: stepsInfoMap[step].icon,
            },
          }}
        >
          {getStepLabel(step)}
        </StepLabel>
      </Step>
    );
  });

  return (
    <Stepper
      alternativeLabel
      activeStep={orderedSteps.indexOf(activeStep)}
      sx={sx}
    >
      {steps}
    </Stepper>
  );
}
